import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiFileText, FiPlus, FiEdit3, FiTrash2, FiX, FiSearch, FiCheck,
} from 'react-icons/fi';
import {
  listarProcuracoes, listarTiposProcuracao, criarProcuracao,
  atualizarProcuracao, excluirProcuracao, faixaProcuracao,
} from '../../../services/procuracoes';
import { listarClientes } from '../../../services/clientes';
import {
  formatarData, textoPrazo, CardsResumo, clientesDoFiltro, itensDoAlerta, ModalAlerta,
} from './comum';
import styles from './Procuracoes.module.css';
import useEsc from '../../../hooks/useEsc';

/* As duas abas (CPF / CNPJ) viraram duas linhas dentro do mesmo cliente:
   assim o cliente 303 aparece uma vez só, com as procurações do CPF em
   cima e as do CNPJ embaixo, em vez de ter que alternar de aba pra ver. */
const OUTORGADOS = [
  { id: 'cpf',  label: 'CPF' },
  { id: 'cnpj', label: 'CNPJ' },
];

/* sugestões no formulário -- o campo é livre, o usuário pode cadastrar
   qualquer nome; estas só aparecem no autocomplete */
const TIPOS_SUGERIDOS = [
  'RFB', 'FGTS', 'Seguro-Desemprego', 'e-CAC', 'Conectividade Social',
  'GOV.BR', 'Prefeitura', 'SEFAZ', 'Caixa', 'INSS',
];

const CHAVE_ALERTAS = 'procuracoes.alertasDispensados';

/* ─── Formulário ─── */
function ModalProcuracao({ registro, clientes, tipos, onFechar, onSalvo }) {
  useEsc(onFechar);
  const editando = !!registro?.id;
  const [form, setForm] = useState({
    cliente_id: registro?.clienteId || '',
    tipo:       registro?.tipo || '',
    outorgado:  registro?.outorgado || 'cpf',
    vencimento: registro?.vencimento || '',
    observacao: registro?.observacao || '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })); }

  async function salvar() {
    setSalvando(true); setErro('');
    try {
      if (editando) {
        const { tipo, outorgado, vencimento, observacao } = form;
        await atualizarProcuracao(registro.id, { tipo, outorgado, vencimento, observacao });
      } else {
        await criarProcuracao(form);
      }
      onSalvo();
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  const sugestoes = [...new Set([...tipos, ...TIPOS_SUGERIDOS])].sort();

  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <h3 className={styles.modalTitulo}>{editando ? 'Editar procuração' : 'Nova procuração'}</h3>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>

        <div className={styles.modalCorpo}>
          <label className={styles.campoLabel}>Cliente</label>
          {editando ? (
            <div className={styles.valorFixo}>{registro.codigo} · {registro.clienteNome}</div>
          ) : (
            <select className={styles.campoInput} value={form.cliente_id}
              onChange={e => set('cliente_id', e.target.value)}>
              <option value="">Selecione...</option>
              {clientes.map(c => (
                <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
              ))}
            </select>
          )}

          <label className={styles.campoLabel}>Tipo da procuração</label>
          <input className={styles.campoInput} list="tipos-procuracao" value={form.tipo}
            onChange={e => set('tipo', e.target.value)} placeholder="RFB, FGTS, Seguro-Desemprego..." />
          <datalist id="tipos-procuracao">
            {sugestoes.map(t => <option key={t} value={t} />)}
          </datalist>

          <label className={styles.campoLabel}>Outorgada para</label>
          <div className={styles.radios}>
            {OUTORGADOS.map(a => (
              <label key={a.id} className={styles.radio}>
                <input type="radio" name="outorgado" checked={form.outorgado === a.id}
                  onChange={() => set('outorgado', a.id)} />
                {a.label}
              </label>
            ))}
          </div>

          <label className={styles.campoLabel}>Vencimento</label>
          <input className={styles.campoInput} type="date" value={form.vencimento}
            onChange={e => set('vencimento', e.target.value)} />
          <p className={styles.dica}>Deixe em branco se a procuração for por prazo indeterminado.</p>

          <label className={styles.campoLabel}>Observação</label>
          <textarea className={styles.campoTextarea} rows={2} value={form.observacao}
            onChange={e => set('observacao', e.target.value)} />

          {erro && <p className={styles.erro}>{erro}</p>}
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={onFechar}>Cancelar</button>
          <button type="button" className={styles.btnPrimario} onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Tela ─── */
function Procuracoes() {
  const [grupos, setGrupos]     = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tipos, setTipos]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]         = useState('');
  const [busca, setBusca]       = useState('');
  const [filtro, setFiltro]     = useState(null); // card de resumo clicado
  const [editando, setEditando] = useState(null);
  const [alertaFechado, setAlertaFechado] = useState(false);
  /* botão 'Editar' geral: fora dele os quadros não mostram ação nenhuma,
     o que deixa os blocos mais curtos e a leitura mais limpa */
  const [modoEdicao, setModoEdicao] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    return listarProcuracoes()
      .then(r => setGrupos(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  /* vindo do onboarding ("Gerar procuração RFB agora? Sim"): abre o
     formulário já com o cliente e o tipo; limpa a URL depois */
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!q.get('cliente')) return;
    setEditando({ clienteId: q.get('cliente'), tipo: q.get('tipo') || '' });
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);
  useEffect(() => {
    listarClientes().then(setClientes).catch(() => {});
    listarTiposProcuracao().then(r => setTipos(r.items || [])).catch(() => {});
  }, []);

  /* lista plana (CPF e CNPJ juntos) -- é o que os cards e o alerta contam */
  const todas = useMemo(() => {
    const plano = [];
    grupos.forEach(g => g.procuracoes.forEach(p => plano.push({
      ...p, clienteId: g.clienteId, codigo: g.codigo, clienteNome: g.nome,
      descricao: `${p.tipo} · ${p.outorgado.toUpperCase()}`,
    })));
    return plano;
  }, [grupos]);

  const alertas = useMemo(() => itensDoAlerta(todas, CHAVE_ALERTAS), [todas]);

  const q = busca.trim().toLowerCase();
  const doFiltro = clientesDoFiltro(todas, filtro);
  const visiveis = grupos.filter(g => {
    if (doFiltro && !doFiltro.has(g.clienteId)) return false;
    if (!q) return true;
    return g.nome.toLowerCase().includes(q) || (g.codigo || '').includes(q)
      || g.procuracoes.some(p => p.tipo.toLowerCase().includes(q));
  });

  async function excluir(p, grupo) {
    if (!window.confirm(`Excluir a procuração "${p.tipo}" (${p.outorgado.toUpperCase()}) de ${grupo.nome}?`)) return;
    try {
      await excluirProcuracao(p.id);
      carregar();
    } catch (e) { setErro(e.message); }
  }

  const totalProcuracoes = visiveis.reduce((n, g) => n + g.procuracoes.length, 0);

  function quadro(p, g) {
    return (
      <div key={p.id} className={`${styles.card} ${styles[faixaProcuracao(p.diasRestantes)]}`}>
        <div className={styles.cardTexto}>
          <span className={styles.cardTipo}>{p.tipo}</span>
          <span className={styles.cardPrazo}>
            {p.vencimento ? `${formatarData(p.vencimento)} · ${textoPrazo(p.diasRestantes)}` : 'sem vencimento'}
          </span>
        </div>
        {modoEdicao && (
          <div className={styles.cardAcoes}>
            <button type="button" className={styles.btnIcone} title="Editar"
              onClick={() => setEditando({ ...p, clienteId: g.clienteId, codigo: g.codigo, clienteNome: g.nome })}>
              <FiEdit3 size={12} />
            </button>
            <button type="button" className={styles.btnIcone} title="Excluir"
              onClick={() => excluir(p, g)}>
              <FiTrash2 size={12} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcone}><FiFileText size={20} /></div>
        <div className={styles.headerTexto}>
          <h1 className={styles.titulo}>Procurações</h1>
        </div>
        <div className={styles.buscaWrap}>
          <FiSearch size={14} className={styles.buscaIcone} />
          <input className={styles.campoInput} type="search" placeholder="Buscar cliente ou tipo..."
            value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <button type="button" className={styles.btnPrimario} onClick={() => setEditando({})}>
          <FiPlus size={14} /> Nova procuração
        </button>
        <button type="button"
          className={modoEdicao ? styles.btnEditarAtivo : styles.btnEditar}
          onClick={() => setModoEdicao(v => !v)}>
          {modoEdicao ? <><FiCheck size={13} /> Concluir</> : <><FiEdit3 size={13} /> Editar</>}
        </button>
      </div>

      <CardsResumo itens={todas} totalClientes={grupos.length} filtro={filtro} onFiltro={setFiltro} />

      <div className={styles.conteudo}>
        <div className={styles.barraContador}>
          <span className={styles.contador}>
            {visiveis.length} {visiveis.length === 1 ? 'cliente' : 'clientes'} · {totalProcuracoes} {totalProcuracoes === 1 ? 'procuração' : 'procurações'}
          </span>
        </div>

        {erro && <p className={styles.erro}>{erro}</p>}

        {carregando ? (
          <p className={styles.vazio}>Carregando...</p>
        ) : visiveis.length === 0 ? (
          <p className={styles.vazio}>
            {grupos.length === 0
              ? 'Nenhuma procuração cadastrada ainda.'
              : 'Nenhum cliente nessa condição.'}
          </p>
        ) : (
          <div className={styles.lista}>
            {visiveis.map(g => (
              <div key={g.clienteId} className={`${styles.linhaCliente} ${styles.linhaClienteEmpilhada}`}>
                <div className={styles.colCliente}>
                  <span className={styles.codigo}>{g.codigo}</span>
                  <span className={styles.nomeCliente}>{g.nome}</span>
                  {g.pendente && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: '#92400e', background: '#fef3c7', borderRadius: 5, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '0.03em' }} title="Cliente vindo do onboarding: cadastre a procuração">Pendente</span>}
                </div>
                <div className={styles.outorgados}>
                  {OUTORGADOS.map(o => {
                    const daLinha = g.procuracoes.filter(p => p.outorgado === o.id);
                    return (
                      <div key={o.id} className={styles.linhaOutorgado}>
                        <span className={styles.seloOutorgado}>{o.label}</span>
                        <div className={styles.cards}>
                          {daLinha.length
                            ? daLinha.map(p => quadro(p, g))
                            : <span className={styles.semItens}>nenhuma</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editando && (
        <ModalProcuracao
          registro={editando}
          clientes={clientes}
          tipos={tipos}
          onFechar={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            carregar();
            listarTiposProcuracao().then(r => setTipos(r.items || [])).catch(() => {});
          }}
        />
      )}

      {!alertaFechado && alertas.length > 0 && (
        <ModalAlerta itens={alertas} titulo="Procurações vencendo" singular="procuração"
          plural="procurações" chave={CHAVE_ALERTAS} onFechar={() => setAlertaFechado(true)} />
      )}
    </div>
  );
}

export default Procuracoes;
